type TabButton = HTMLButtonElement;
type TabPanel = HTMLElement;

const SELECTOR_ROOT = '.bloc-actions-tabs';
const SELECTOR_TAB = '[role="tab"]';
const SELECTOR_PANEL = '[role="tabpanel"]';

function getTabs(root: HTMLElement): TabButton[] {
	return Array.from(root.querySelectorAll<TabButton>(SELECTOR_TAB));
}

function getPanels(root: HTMLElement): TabPanel[] {
	return Array.from(root.querySelectorAll<TabPanel>(SELECTOR_PANEL));
}

function getPanelByTab(tab: TabButton): TabPanel | null {
	const panelId = tab.getAttribute('aria-controls');

	if (!panelId) {
		return null;
	}

	return document.getElementById(panelId);
}

function activateTab(root: HTMLElement, selectedTab: TabButton, focus = true): void {
	const tabs = getTabs(root);
	const panels = getPanels(root);

	tabs.forEach((tab) => {
		const isSelected = tab === selectedTab;

		tab.setAttribute('aria-selected', String(isSelected));
		tab.tabIndex = isSelected ? 0 : -1;
	});

	panels.forEach((panel) => {
		panel.hidden = true;
	});

	const selectedPanel = getPanelByTab(selectedTab);

	if (selectedPanel) {
		selectedPanel.hidden = false;
	}

	if (focus) {
		selectedTab.focus();
	}
}

function getNextTab(tabs: TabButton[], currentTab: TabButton): TabButton {
	const currentIndex = tabs.indexOf(currentTab);
	const nextIndex = currentIndex + 1;

	return tabs[nextIndex] ?? tabs[0];
}

function getPreviousTab(tabs: TabButton[], currentTab: TabButton): TabButton {
	const currentIndex = tabs.indexOf(currentTab);
	const previousIndex = currentIndex - 1;

	return tabs[previousIndex] ?? tabs[tabs.length - 1];
}

function onTabKeydown(event: KeyboardEvent, root: HTMLElement, currentTab: TabButton): void {
	const tabs = getTabs(root);

	if (!tabs.length) {
		return;
	}

	let targetTab: TabButton | null = null;

	switch (event.key) {
		case 'ArrowRight':
		case 'ArrowDown':
			targetTab = getNextTab(tabs, currentTab);
			break;

		case 'ArrowLeft':
		case 'ArrowUp':
			targetTab = getPreviousTab(tabs, currentTab);
			break;

		case 'Home':
			targetTab = tabs[0];
			break;

		case 'End':
			targetTab = tabs[tabs.length - 1];
			break;

		default:
			return;
	}

	event.preventDefault();

	if (targetTab) {
		activateTab(root, targetTab);
	}
}

function initTabs(root: HTMLElement): void {
	const tabs = getTabs(root);

	if (!tabs.length) {
		return;
	}

	const selectedTab =
		tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')
		?? tabs[0];

	activateTab(root, selectedTab, false);

	tabs.forEach((tab) => {
		tab.addEventListener('click', () => {
			activateTab(root, tab, false);
		});

		tab.addEventListener('keydown', (event) => {
			onTabKeydown(event, root, tab);
		});
	});
}

export function initBlocActionsTabs(): void {
	const roots = document.querySelectorAll<HTMLElement>(SELECTOR_ROOT);

	roots.forEach((root) => {
		initTabs(root);
	});
}
